import type {
  ProductSKU,
  OptionType,
  MethodsOfUseForm,
  SampleRequest,
  SampleRequestDetail,
  SampleRequestIndividualItem,
  IPriceTierPaginatedOutput,
} from "../../types/types";
import type { PIMProduct } from "../../types/types.PIM";
import { Controller } from "react-hook-form";
import useSWR from "swr";
import { endpoints } from "../../endpoints";
import React, { useContext, useEffect } from "react";
import {
  convertProductSKUToOption,
  makeUrlWithParams,
  useCurrencySymbol,
  useStoreState,
} from "../../util/util";
import { SelectBoxV2 } from "../SelectBoxV2/SelectBoxV2";
import { TextField } from "../TextFields/TextFields";
import {
  calculateTotalQuantity,
  formatTotalQuantityString,
} from "../quoteCart/BuyerQuoteItemForm";
import { TransactionItem } from "./TransactionItem";
import { TransactionItemTopLeftSampleRequest } from "./TransactionItemTopLeft";
import { TransactionItemInfoBlock } from "./TransactionItemInfoBlock";
import {
  makeNumberOfUnitsLabel,
  makePricePerUnitString,
  makeSkuLabel,
} from "./TransactionItemUtils";
import styled from "styled-components/macro";
import { useTranslation } from "react-i18next";
import { CurrencyInput } from "../CurrencyInput/CurrencyInput";
import { formatPrice } from "../../util/util-components";
import {
  calculatePriceForQuantity,
  calculatePriceTiersPricePerUom,
} from "../../util/QuotesAndOrders";
import { useProductSkuListPrice } from "../../util/SkuUtils";
import { Notifications } from "../Notifications/NotificationsContext";
import type { ItemDetailRecord } from "./QuoteItemCardSellerResponding";

export type { ItemDetailRecord };

const BottomRightContainer = styled.div`
  display: flex;
`;

const InputContainer = styled.div`
  margin-left: 10px;
  width: 140px;
`;

const InputContainerSku = styled(InputContainer)`
  width: 200px;
`;

const InputContainerPPU = styled(InputContainer)`
  width: 160px;
`;

/**
 * Sample request item card shown to the seller when they are reviewing a
 * request. Optional pricing is shown when payments on samples is enabled.
 */
export const SampleRequestItemCardSellerResponding = ({
  sample,
  item,
  index,
  methodsOfUseForm,
  inputIdSku,
  inputIdCustomSku,
  isBuyerSubmittedPricedSample,
  inputIdNumberOfUnits,
  inputIdPricePerUnit,
  enablePayments = false,
  itemDetail,
  propagateItemDetails,
  deliveryTermId,
  paymentTermId,
}: {
  sample: SampleRequest;
  item: SampleRequestDetail;
  index: number;
  methodsOfUseForm: MethodsOfUseForm;
  inputIdSku: string;
  inputIdCustomSku: string;
  isBuyerSubmittedPricedSample: boolean;
  inputIdNumberOfUnits: string;
  inputIdPricePerUnit: string;
  enablePayments?: boolean;
  itemDetail?: { totalPrice: number | null; hasPriceTiers: boolean };
  propagateItemDetails?: (itemDetailRecord: ItemDetailRecord) => void;
  deliveryTermId?: string;
  paymentTermId?: string;
}) => {
  const { t } = useTranslation();
  const { storefront_id } = useStoreState();
  const { notifyError } = useContext(Notifications);
  const currencySymbol = useCurrencySymbol(
    item.currency ?? sample.currency ?? "USD"
  );

  const { register, errors, formState, watch, control, setValue } =
    methodsOfUseForm;

  const skuOption: OptionType<ProductSKU> | undefined = watch(inputIdSku);
  const numberOfUnits: string = watch(inputIdNumberOfUnits);
  const pricePerUnit: string = watch(inputIdPricePerUnit);

  const { data: product } = useSWR<PIMProduct>(
    item.product_id
      ? endpoints.v2_storefronts_id_pim_products_id(
          storefront_id,
          item.product_id
        )
      : ""
  );
  const sku = skuOption?.value;

  const { data: priceTiersData } = useSWR<IPriceTierPaginatedOutput>(
    enablePayments && skuOption && deliveryTermId && paymentTermId
      ? makeUrlWithParams(endpoints.v1_priceTiers(), {
          seller_id: sample.seller_id,
          buyer_id: sample.buyer_id,
          destination_id: sample.shipping_address?.id,
          product_sku_id: skuOption.value.id,
          delivery_term_id: deliveryTermId,
          payment_term_id: paymentTermId,
          currency: item.currency ?? sample.currency,
        })
      : null,
    {
      onError: (error) => {
        notifyError(t("There was an error checking for price tiers"), {
          error,
        });
      },
    }
  );

  const { list_prices_as_price_tiers, list_prices_exist } =
    useProductSkuListPrice(item.product_id, skuOption?.value?.number);

  const priceTiers = priceTiersData?.data;
  const itemHasPriceTiers = !!(priceTiers && priceTiers.length > 0);

  const priceTiersPricePerUom =
    itemHasPriceTiers && priceTiers && numberOfUnits
      ? calculatePriceTiersPricePerUom(priceTiers, parseFloat(numberOfUnits))
      : null;

  const list_prices_price_per_uom =
    list_prices_exist && numberOfUnits
      ? calculatePriceTiersPricePerUom(
          list_prices_as_price_tiers,
          parseFloat(numberOfUnits)
        )
      : null;

  useEffect(() => {
    if (!enablePayments) {
      return;
    }
    if (itemHasPriceTiers) {
      setValue(inputIdPricePerUnit, priceTiersPricePerUom || "");
    } else if (list_prices_exist) {
      setValue(inputIdPricePerUnit, list_prices_price_per_uom || "");
    }
  }, [
    enablePayments,
    itemHasPriceTiers,
    priceTiersPricePerUom,
    inputIdPricePerUnit,
    setValue,
    list_prices_exist,
    list_prices_price_per_uom,
  ]);

  const totalQuantity = calculateTotalQuantity(
    sku?.package_volume,
    numberOfUnits
  );
  const totalQuantityString =
    totalQuantity === null
      ? "--"
      : formatTotalQuantityString(sku?.packaging_unit?.name, totalQuantity);

  const newTotalItemPrice = (() => {
    if (!enablePayments) {
      return null;
    }
    if (itemHasPriceTiers && priceTiersPricePerUom && totalQuantity) {
      return calculatePriceForQuantity(priceTiersPricePerUom, totalQuantity);
    }
    if (list_prices_exist && list_prices_price_per_uom && totalQuantity) {
      return calculatePriceForQuantity(
        list_prices_price_per_uom,
        totalQuantity
      );
    }
    if (totalQuantity && pricePerUnit) {
      return calculatePriceForQuantity(pricePerUnit, totalQuantity);
    }
    if (totalQuantity) {
      // Calculate based on user input.
      return calculatePriceForQuantity(pricePerUnit, totalQuantity);
    }

    return null;
  })();

  const itemDetailTotalPrice = itemDetail?.totalPrice;

  useEffect(() => {
    if (!enablePayments || !propagateItemDetails) {
      return;
    }
    if (itemDetailTotalPrice !== newTotalItemPrice) {
      propagateItemDetails({
        [item.id]: {
          totalPrice: newTotalItemPrice,
          hasPriceTiers: !!(itemHasPriceTiers && priceTiersPricePerUom),
        },
      });
    }
  }, [
    enablePayments,
    propagateItemDetails,
    item.id,
    itemDetailTotalPrice,
    newTotalItemPrice,
    itemHasPriceTiers,
    priceTiersPricePerUom,
  ]);

  const topRightCol2 =
    enablePayments || sample.enable_payments ? (
      <TransactionItemInfoBlock
        alignRight={true}
        main={
          newTotalItemPrice
            ? formatPrice(
                newTotalItemPrice,
                item.currency ?? sample.currency ?? "USD"
              )
            : "--"
        }
        lines={[
          makePricePerUnitString(
            itemHasPriceTiers ? String(priceTiersPricePerUom) : pricePerUnit,
            item.currency ?? sample.currency ?? "USD",
            sku?.packaging_unit?.name
          ),
          totalQuantityString,
        ]}
      />
    ) : (
      <TransactionItemInfoBlock
        main={totalQuantityString}
        lines={[makeSkuLabel(sku), makeNumberOfUnitsLabel(numberOfUnits, t)]}
      />
    );
  const requestedTotal =
    calculateTotalQuantity(item.sku?.package_volume, item.no_of_units) ?? 0;
  const requestedQuantity =
    formatTotalQuantityString(item.sku?.packaging_unit.name, requestedTotal) ??
    "--";

  const skuInput = (
    <InputContainerSku>
      <Controller
        as={SelectBoxV2}
        control={control}
        name={inputIdSku}
        placeholder={"SKU"}
        options={product?.product_skus.reduce<OptionType<ProductSKU>[]>(
          (acc, sku) => {
            if (sku.is_sample) {
              acc.push(convertProductSKUToOption(sku));
            }
            return acc;
          },
          []
        )}
        defaultValue={
          item.sku && item.sku.kind === "Product SKU"
            ? convertProductSKUToOption(item.sku)
            : undefined
        }
        disabled={!isBuyerSubmittedPricedSample}
        rules={{ required: true }}
        errors={errors}
        formState={formState}
      />
    </InputContainerSku>
  );

  const numberOfUnitsInput = (
    <InputContainer>
      <TextField
        name={inputIdNumberOfUnits}
        label={t("No. of Units")}
        defaultValue={item.no_of_units ?? undefined}
        theref={register({ required: true })}
        errors={errors}
        formState={formState}
        min={1}
        disabled={!isBuyerSubmittedPricedSample}
        type="number"
      />
    </InputContainer>
  );

  const pricePerUnitInput = (
    <InputContainerPPU>
      <CurrencyInput
        name={inputIdPricePerUnit}
        label={`Price (${currencySymbol}/UoM)`}
        defaultValue={item.price_per_unit ?? undefined}
        theref={register({ required: true })}
        errors={errors}
        formState={formState}
        testid={`sample-price-per-unit-input-${index}`}
        disabled={itemHasPriceTiers || !isBuyerSubmittedPricedSample}
        type="number"
      />
    </InputContainerPPU>
  );

  const bottomRightCol1 = enablePayments && (
    <BottomRightContainer>
      {skuInput}
      {numberOfUnitsInput}
      {pricePerUnitInput}
    </BottomRightContainer>
  );

  const TransactionItemInfoBlockAnnualUsage = ({
    item,
  }: {
    item: SampleRequestDetail | SampleRequestIndividualItem;
  }) => {
    const { t } = useTranslation();

    const totalQuantityString = `${item.total_quantity || "--"} ${
      item.total_quantity_packaging_unit?.name || "--"
    }`;

    return (
      <TransactionItemInfoBlock
        heading={t("Potential Annual Usage")}
        main={totalQuantityString}
        lines={[item.purpose]}
      />
    );
  };

  return (
    <TransactionItem
      topLeft={
        <TransactionItemTopLeftSampleRequest item={item} index={index} />
      }
      topRightCol1={<TransactionItemInfoBlockAnnualUsage item={item} />}
      topRightCol2={topRightCol2}
      bottomLeftCol1={
        <TransactionItemInfoBlock
          heading={t("Requested Details")}
          main={
            item.no_of_units
              ? requestedQuantity
              : formatTotalQuantityString(
                  item.sample_quantity_packaging_unit?.name,
                  item.sample_quantity
                )
          }
          lines={[
            ...(item.sku?.kind ? [makeSkuLabel(item.sku)] : []),
            ...(item.no_of_units
              ? [makeNumberOfUnitsLabel(item.no_of_units, t)]
              : []),
          ]}
          warningLine={item.sku?.kind ?? undefined}
        />
      }
      bottomRightAlignTop={bottomRightCol1}
      // bottomLeftCol2={
      //   <TransactionItemInfoBlockAnnualUsage item={item} />}
      requestType="sample"
      requestId={sample.id}
      itemId={item.id}
      productId={item.product_id}
      buyerId={sample.buyer_id}
    />
  );
};
